import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BTN } from "@/components/common/btn";

let mountSeq = 0;

// Guards the open builder: browser Back/Forward, in-app links and tab close ask "Save progress?" while edits are unsaved.
// onSave must resolve truthy when the save actually succeeded; onDiscard cancels any pending autosave.
export function useUnsavedGuard({ dirty, canSave = true, onSave, onDiscard, onLeave }) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null); // { kind: "back" | "link", href }
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const leavingRef = useRef(false);
  const cb = useRef({ onSave, onDiscard, onLeave });
  cb.current = { onSave, onDiscard, onLeave };

  useEffect(() => {
    const mySeq = ++mountSeq;
    if (!window.history.state?.builderGuard) window.history.pushState({ builderGuard: true }, "");
    const onPop = () => {
      if (leavingRef.current) return;
      if (dirtyRef.current) {
        window.history.pushState({ builderGuard: true }, "");
        setPending({ kind: "back" });
      } else {
        leavingRef.current = true;
        cb.current.onLeave();
      }
    };
    const onClick = (e) => {
      if (!dirtyRef.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey) return;
      const a = e.target.closest?.("a[href]");
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      const href = a.getAttribute("href");
      if (!href || !href.startsWith("/") || href.startsWith("//")) return;
      e.preventDefault();
      e.stopPropagation();
      setPending({ kind: "link", href });
    };
    const onUnload = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("popstate", onPop);
    document.addEventListener("click", onClick, true);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("popstate", onPop);
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("beforeunload", onUnload);
      // Consume our dummy entry when the builder closes any other way (Close button, link, save-and-leave).
      // Deferred so a StrictMode remount (dev only) doesn't pop the entry the remount still relies on.
      setTimeout(() => {
        if (mySeq === mountSeq && window.history.state?.builderGuard) {
          leavingRef.current = true;
          window.history.back();
        }
      }, 0);
    };
  }, []);

  const go = () => {
    const p = pending;
    setPending(null);
    if (!p) return;
    dirtyRef.current = false;
    leavingRef.current = true;
    if (p.kind === "link") {
      // Pop the dummy entry first so Back from the next page returns to the list, then route.
      window.history.back();
      setTimeout(() => navigate(p.href), 40);
    } else {
      cb.current.onLeave();
    }
  };
  const discardAndGo = () => { cb.current.onDiscard?.(); go(); };
  const saveAndGo = async () => {
    let ok = false;
    try { ok = !!(await cb.current.onSave()); } catch { ok = false; }
    if (ok) go(); else setPending(null); // validation/network failure: stay, the save already toasted why
  };

  return (
    <AlertDialog open={!!pending} onOpenChange={(v) => { if (!v) setPending(null); }}>
      <AlertDialogContent data-testid="unsaved-guard-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{canSave ? "Save progress?" : "This proposal isn't saved yet"}</AlertDialogTitle>
          <AlertDialogDescription>
            {canSave
              ? "You have unsaved changes on this proposal. Save them before leaving so nothing is lost."
              : "Proposals save automatically once they have a name and a customer. Leave now and this one is discarded, or stay and finish those two fields."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="unsaved-guard-stay">Stay</AlertDialogCancel>
          <AlertDialogAction className={BTN.danger} onClick={discardAndGo} data-testid="unsaved-guard-discard">{canSave ? "No, Discard" : "Leave & Discard"}</AlertDialogAction>
          {canSave && <AlertDialogAction className={BTN.write} onClick={saveAndGo} data-testid="unsaved-guard-save">Yes, Save</AlertDialogAction>}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
